import { formatNumber } from "pages/MatchDetail/MatchedOdds";
import React, { useState } from "react";
import { Table } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./style.css";

interface FancyPLData {
  user_name: string;
  sport_name: string;
  uplineProfitLoss: number;
  total: number;
  commission: number;
  type: number;
  sport_id: string;
  total_net_pl: string;
  series_name: string;
  match_name: string;
  market_name: string;
  winner_name: string;
  settle_date_time: string;
  event_id: string;
  match_id:string
}

interface FancyPLTableProps {
  fancyPLData: FancyPLData[];
  searchTerm?: string;
  onClickRow?: (
    match_id: string,
    market_id: string,
    match_name: string,
    sport_name: string,
    id: string,
    type: string
  ) => void;
}

const formatDateTime = (dateStr: string) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(date);
};

const PlUserTable: React.FC<FancyPLTableProps> = ({
  fancyPLData,
  searchTerm = "",
  onClickRow,
}) => {
  const [sortKey, setSortKey] = useState<keyof FancyPLData | "">("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const handleSort = (key: keyof FancyPLData) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const getSortClass = (key: keyof FancyPLData) => {
    const isActive = sortKey === key;
    const upClass = isActive && sortOrder === "asc" ? "text-dark" : "text-muted";
    const downClass = isActive && sortOrder === "desc" ? "text-dark" : "text-muted";

    return (
      <span className="d-flex flex-column ms-2">
        <i className={`bi bi-caret-up-fill ${upClass}`} style={{ lineHeight: 0.6 }} />
        <i className={`bi bi-caret-down-fill ${downClass}`} style={{ lineHeight: 0.6 }} />
      </span>
    );
  };

  const sortedData = sortKey
    ? [...fancyPLData].sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];

        if (typeof aVal === "string" && typeof bVal === "string") {
          if (sortKey === "settle_date_time") {
            const aDate = new Date(aVal).getTime();
            const bDate = new Date(bVal).getTime();
            return sortOrder === "asc" ? aDate - bDate : bDate - aDate;
          }
          return sortOrder === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        const aNum = typeof aVal === "number" ? aVal : parseFloat(aVal as string);
        const bNum = typeof bVal === "number" ? bVal : parseFloat(bVal as string);

        if (isNaN(aNum) || isNaN(bNum)) return 0;
        return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
      })
    : fancyPLData;

  const filteredData = searchTerm
    ? sortedData.filter((item) =>
        (item.user_name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (item.sport_name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (item.series_name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (item.market_name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (item.winner_name?.toLowerCase() || "").includes(searchTerm.toLowerCase())
      )
    : sortedData;

  return (
    <div className="table-responsive">
      <Table striped hover responsive className="table-centered align-middle">
        <thead className="table-light">
          <tr>
            <th onClick={() => handleSort("user_name")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>User Name</span>
                {getSortClass("user_name")}
              </div>
            </th>
            <th onClick={() => handleSort("sport_name")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Sport Name</span>
                {getSortClass("sport_name")}
              </div>
            </th>
            <th onClick={() => handleSort("series_name")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Event Name</span>
                {getSortClass("series_name")}
              </div>
            </th>
            <th onClick={() => handleSort("market_name")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Market Name</span>
                {getSortClass("market_name")}
              </div>
            </th>
            <th onClick={() => handleSort("winner_name")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Result</span>
                {getSortClass("winner_name")}
              </div>
            </th>
            <th onClick={() => handleSort("total")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Profit/Loss</span>
                {getSortClass("total")}
              </div>
            </th>
            <th onClick={() => handleSort("commission")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Commission</span>
                {getSortClass("commission")}
              </div>
            </th>
            <th onClick={() => handleSort("settle_date_time")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Settle Date Time</span>
                {getSortClass("settle_date_time")}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((item, index) => (
            <tr
              key={index}
              onClick={() =>
                onClickRow?.(
                  item.match_id,
                  item.sport_id,
                  item.match_name,
                  item.sport_name,
                  item.market_name,
                  String(item.type)
                )
              }
              style={{ cursor: "pointer" }}
            >
              <td>{item.user_name}</td>
              <td>{item.sport_name}</td>
              <td>{item.match_name}</td>
              <td>{item.market_name}</td>
              <td>{item.winner_name}</td>
              <td className={item.total < 0 ? "text-danger" : "text-success"}>
                {formatNumber(String(item.total))}
              </td>
              <td>{item.commission}</td>
              <td>{formatDateTime(item.settle_date_time)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default PlUserTable;
